// cSpell:ignore betaaluitnodigingen eigenaar versturen
import {
  getName,
  IPerson,
} from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { isEqual, isUndefined, omitBy } from "lodash";
import { findPayerRequest, IPayerRequestRef } from "../../../costPayment.utils";
import { canPayPerOwner, IOwnerPayGroups } from "../../../ownerPayGroups.utils";
import { SettlementAction, SettlementSnapshot } from "../../../types";

// Below this a balance is rounding noise: nobody gets a payment order for it.
export const MIN_PAYABLE_AMOUNT = 0.005;
// One payment form for several tenants only pays off from two of them.
const MIN_TENANTS_ALL_AT_ONCE = 2;

export interface IOwingTenant {
  contractId: string;
  tenantName: string;
  amount: number;
  // The payer frozen on confirm (absent before the settlement is saved).
  payerId?: number;
}

// A tenant to bill in the payment step, with the payer and the property the
// payment order goes on.
export interface IPayShare extends IOwingTenant {
  payerName?: string;
  propertyId: number;
  unitLabel?: string;
  // The payment's description, in the payer's language.
  description: string;
}

// A created payment order as the store holds it: its payer, and its payment
// requests when the response already embeds them (the backend usually
// generates them later, so a share's request id is often not known yet; its
// status is then read per order, one order per tenant).
export interface ICreatedOrderRef {
  payerId?: number | null;
  // The payer's account, which the API returns with the order.
  payer?: IPerson | null;
  paymentRequests?: IPayerRequestRef[];
}

export interface ICreatedOrderLinks {
  orderByContract: Record<string, number>;
  requestByContract: Record<string, number>;
  // Who each share's order bills: also the payer the payment form asked for.
  payerByContract: Record<string, number>;
}

// A single-choice OptionList reports an empty selection when its selected
// option is clicked again. That is not a choice: the current one stays (it
// used to flip "create payment orders" to "no payment orders").
export const handleSingleChoice =
  (apply: (value: string) => void) => (selection: string[]) => {
    if (selection[0]) apply(selection[0]);
  };

// Who owes what once the settlement is saved. Read from the frozen snapshot,
// never from the live calculation: a payment must bill exactly the amount on
// the confirmed statement, whatever changed in the wizard since.
export const getOwingTenantsFromSnapshot = (
  snapshot: SettlementSnapshot,
): IOwingTenant[] =>
  snapshot.actions
    .filter(
      action => action.type === "invite" && action.amount > MIN_PAYABLE_AMOUNT,
    )
    .map(action => {
      const frozen = snapshot.perContract.find(
        pc => pc.contractId === action.contractId,
      );
      return {
        contractId: action.contractId,
        tenantName: frozen?.tenantName ?? "",
        amount: action.amount,
        payerId: frozen?.payerAccountId,
      };
    });

// Index of the first share at or after `from` whose tenant has no payment
// order yet, or -1 when every one of them has. A tenant who already has one
// is never offered a second.
export const getNextOpenShareIndex = (params: {
  shares: { contractId: string }[];
  orderByContract: Record<string, number>;
  from: number;
}): number => {
  const { shares, orderByContract, from } = params;
  for (let index = Math.max(0, from); index < shares.length; index += 1) {
    if (orderByContract[shares[index].contractId] == null) return index;
  }
  return -1;
};

// Links freshly created payment orders to the shares they were created for.
// Every tenant gets an order of their own, billed to their payer: a share
// takes the first order created for its payer. The shares left (the payment
// form asked for their payer) take the orders left in creation order (the
// bulk path creates them in share order), when those line up one to one and
// do not bill someone else. A share whose order cannot be told apart stays
// unlinked rather than carry another tenant's order and payment status.
export const linkCreatedOrders = (params: {
  shares: { contractId: string; payerId?: number }[];
  createdOrderIds: number[];
  ordersById: Record<number, ICreatedOrderRef | undefined>;
}): ICreatedOrderLinks => {
  const { shares, createdOrderIds, ordersById } = params;
  const links: ICreatedOrderLinks = {
    orderByContract: {},
    requestByContract: {},
    payerByContract: {},
  };
  const payerOfOrder = (orderId: number) =>
    ordersById[orderId]?.payerId ?? undefined;

  const unclaimedOrderIds = [...createdOrderIds];
  const claim = (link: { contractId: string; orderId: number }) => {
    links.orderByContract[link.contractId] = link.orderId;
    unclaimedOrderIds.splice(unclaimedOrderIds.indexOf(link.orderId), 1);
  };
  shares.forEach(share => {
    if (share.payerId == null) return;
    const orderId = unclaimedOrderIds.find(
      id => payerOfOrder(id) === share.payerId,
    );
    if (orderId != null) claim({ contractId: share.contractId, orderId });
  });
  const sharesLeft = shares.filter(
    share => links.orderByContract[share.contractId] == null,
  );
  if (sharesLeft.length === unclaimedOrderIds.length) {
    const ordersLeft = [...unclaimedOrderIds];
    sharesLeft.forEach((share, index) => {
      const orderId = ordersLeft[index];
      const orderPayerId = payerOfOrder(orderId);
      const isOtherPayer =
        share.payerId != null &&
        orderPayerId != null &&
        orderPayerId !== share.payerId;
      if (!isOtherPayer) claim({ contractId: share.contractId, orderId });
    });
  }

  shares.forEach(share => {
    const orderId = links.orderByContract[share.contractId];
    if (orderId == null) return;
    const payerId = payerOfOrder(orderId) ?? share.payerId;
    if (payerId != null) links.payerByContract[share.contractId] = payerId;
    // The order is this share's alone, so its payer's request is the share's.
    const ownRequest = findPayerRequest({
      requests: ordersById[orderId]?.paymentRequests ?? [],
      payerId,
    });
    if (ownRequest?.id != null) {
      links.requestByContract[share.contractId] = ownRequest.id;
    }
  });
  return links;
};

// Who each linked share's order bills, by name, as the API returned it with
// the order. For a share whose payer the payment form asked for (a lease
// without a tenant or parent) this is the only place that name is known.
export const getOrderPayerNames = (params: {
  orderByContract: Record<string, number>;
  ordersById: Record<number, ICreatedOrderRef | undefined>;
}): Record<string, string> => {
  const { orderByContract, ordersById } = params;
  const names: Record<string, string> = {};
  Object.entries(orderByContract).forEach(([contractId, orderId]) => {
    const payer = ordersById[orderId]?.payer;
    const name = payer ? getName(payer) : "";
    if (name) names[contractId] = name;
  });
  return names;
};

// Resolves once the browser painted what was rendered before the call: a
// "busy" label set just before heavy synchronous work then shows first.
export const afterNextPaint = (): Promise<void> =>
  new Promise(resolve => {
    requestAnimationFrame(() => setTimeout(resolve, 0));
  });

// The actions of `next` that differ from the stored one at the same place
// (a missing field and an undefined one are the same). A write to a confirmed
// settlement sends only these: the server keeps every action a body leaves
// out, so a view older than what is stored cannot overwrite what another tab
// stored for another tenant (N08), and a big settlement is not sent again.
export const changedSettlementActions = (params: {
  stored: SettlementAction[] | undefined;
  next: SettlementAction[];
}): SettlementAction[] => {
  const { stored = [], next } = params;
  const defined = (action: SettlementAction) => omitBy(action, isUndefined);
  return next.filter((action, index) => {
    const before = stored[index];
    return (
      !before ||
      before.contractId !== action.contractId ||
      !isEqual(defined(before), defined(action))
    );
  });
};

// A tenant's paid or refund mark, set or cleared: that tenant's action alone,
// which is all the write sends (the server refuses a mark sent along with
// other tenants' actions, N08).
export const markedTenantActions = (params: {
  actions: SettlementAction[];
  contractId: string;
  mark: Partial<SettlementAction>;
}): SettlementAction[] =>
  params.actions
    .filter(action => action.contractId === params.contractId)
    .map(action => ({ ...action, ...params.mark }));

// The actions with the payment orders (and requests) just created linked to
// the tenants they bill. A manual "paid" mark no longer applies to a tenant
// who now pays through this payment request.
export const withPaymentLinks = (params: {
  actions: SettlementAction[];
  links: Pick<ICreatedOrderLinks, "orderByContract" | "requestByContract">;
}): SettlementAction[] => {
  const { actions, links } = params;
  return actions.map(action => {
    const paymentOrderId = links.orderByContract[action.contractId];
    if (paymentOrderId == null) return action;
    return {
      ...action,
      paymentOrderId,
      paymentRequestId: links.requestByContract[action.contractId],
      manualPaidAt: undefined,
    };
  });
};

// The settlement wizard's actions with the payment orders and requests it
// created so far. Each link sends all of them again, so one whose own
// (best-effort) save failed is stored by the next link instead of never.
export const withWizardLinks = (params: {
  actions: SettlementAction[];
  links: Pick<ICreatedOrderLinks, "orderByContract" | "requestByContract">;
}): SettlementAction[] => {
  const { actions, links } = params;
  return actions.map(action => ({
    ...action,
    paymentOrderId:
      links.orderByContract[action.contractId] ?? action.paymentOrderId,
    paymentRequestId:
      links.requestByContract[action.contractId] ?? action.paymentRequestId,
  }));
};

// Whether the wizard created a payment order or request the saved settlement
// does not hold yet. Their own saves are best effort, so the wizard stores
// them again before it opens the payment steps and when the broker leaves it.
export const hasPendingWizardLinks = (
  params: Parameters<typeof withWizardLinks>[0],
): boolean => {
  const { actions, links } = params;
  const isUnlinked = (linkedId: number | undefined, storedId?: number) =>
    linkedId != null && linkedId !== storedId;
  return actions.some(
    action =>
      isUnlinked(
        links.orderByContract[action.contractId],
        action.paymentOrderId,
      ) ||
      isUnlinked(
        links.requestByContract[action.contractId],
        action.paymentRequestId,
      ),
  );
};

// Whether the tenants still to invite can share one payment form ("all at
// once"): at least two, each with a known payer that did not change since
// confirming (only a form of their own asks for one), and all on properties
// of one known owner. One form pays every tenant to one beneficiary, so a
// second owner would receive another owner's money (Lewis, 2026-10-05). It
// fails closed while the owners load or when a property has no known owner.
export const canInviteAllAtOnce = (params: {
  shares: { propertyId: number; payerId?: number; hasPayersChanged: boolean }[];
  ownerKeyByProperty: Record<number, string>;
  ownersLoaded: boolean;
}): boolean => {
  const { shares, ownerKeyByProperty, ownersLoaded } = params;
  if (!ownersLoaded || shares.length < MIN_TENANTS_ALL_AT_ONCE) return false;
  if (shares.some(share => share.payerId == null || share.hasPayersChanged)) {
    return false;
  }
  const ownerKeys = new Set(
    shares.map(share => ownerKeyByProperty[share.propertyId] ?? ""),
  );
  return ownerKeys.size === 1 && !ownerKeys.has("");
};

// Whether the tenants still to invite can be invited one owner at a time
// instead ("Alle betaaluitnodigingen versturen per eigenaar", see
// ownerPayGroups.utils): the rules of canInviteAllAtOnce, but per owner. Each
// tenant has a known payer that did not change since confirming, the owners
// are known, and an owner has two of them to invite together.
export const canInvitePerOwner = (params: {
  shares: { payerId?: number; hasPayersChanged: boolean }[];
  grouped: IOwnerPayGroups<unknown>;
  ownersLoaded: boolean;
}): boolean => {
  const { shares, grouped, ownersLoaded } = params;
  if (!ownersLoaded || shares.length < MIN_TENANTS_ALL_AT_ONCE) return false;
  if (shares.some(share => share.payerId == null || share.hasPayersChanged)) {
    return false;
  }
  return canPayPerOwner(grouped);
};
